import type { AxiosInstance } from "axios";
import { appConfig } from "@/config";
import { createLogger } from "@/logging";

/**
 * 시뮬레이션 지연 주입 경계 (rules.md §7).
 *
 * 모든 데이터 통신은 미국 <-> 한국/동남아 round-trip(약 200ms ~ 300ms)을 표준으로
 * 설계한다. 개발 환경에서 그 지연을 항상 체감하도록, axios 요청을 보내기 전에 표준
 * 지연만큼 기다린다. 그래서 빠른 로컬·동일 리전 백엔드에 연결해도 느린 네트워크가 기본이다.
 *
 * Owns:
 * - `appConfig.simulatedLatencyMs`에 따른 요청 전 지연 주입과 1회 안내 로그.
 *
 * Does not own:
 * - 지연 on/off·값 결정. `appConfig`가 소유하고, 프로덕션 빌드에서는 항상 끈다.
 * - timeout·재시도 정책(각 http client가 소유).
 */

const log = createLogger("http/simulatedLatency");

let announced = false;

/** 주어진 axios client의 모든 요청에 표준 지연을 주입한다. 지연이 꺼져 있으면 무시한다. */
export function installSimulatedLatency(client: AxiosInstance): void {
  const latencyMs = appConfig.simulatedLatencyMs;
  if (latencyMs === null) {
    return;
  }

  announceOnce(latencyMs);

  client.interceptors.request.use(async (config) => {
    await delay(latencyMs);
    return config;
  });
}

function announceOnce(latencyMs: number): void {
  if (announced) {
    return;
  }
  announced = true;
  log.warn(
    `Simulated network latency is ON (~${latencyMs}ms per request). ` +
      `Set VITE_SIMULATED_LATENCY=off to disable.`,
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
